/**
 * `getOffersWithStats` — the offer ELIGIBILITY ENGINE.
 *
 * ============================ NOT YET PORTED ==============================
 *
 * This is the one piece of the wallet that is still outstanding, and it is
 * deliberately left as a named boundary rather than half-written, because a
 * partial engine would invent eligibility rules and messages that the original
 * does not have.
 *
 * WHERE IT IS. `recovery-source/live-app-formatted.js`, module **83467**,
 * function **`ey`**, exported as `getOffersWithStats` — roughly 1 070 formatted
 * lines (the four pathway branches, the seven reward branches, the voucher and
 * gift-card availability probes, the applicable-times bookkeeping and the
 * message table). Locate it by symbol: `grep -n "t.getOffersWithStats = ey"`.
 * Its call site is the offers provider, module 80603, and the inputs it takes
 * are exactly the ones `OffersProvider` already assembles.
 *
 * WHY IT IS NOT URGENT, AND WHY IT COULD NOT BE VERIFIED IF IT WERE WRITTEN.
 *
 * 1. `offers`, `vouchers` and `gift_cards` are EMPTY collections across the
 *    whole database — not just for `butt-karahi-main` (verified 2026-08-14).
 *    With no offers, the original's own provider short-circuits before ever
 *    calling this: `x || 0 === H.length ? null : getOffersWithStats(…)`. The
 *    empty result this module returns today is therefore the SAME result the
 *    original produces on this deployment's data, by the original's own code
 *    path — the drawer correctly renders "No offers available right now".
 * 2. The stored `offers` schema (recovered from the POS bundle v1.8.5) predates
 *    several fields the engine reads: `pathway_to_unlock_type_vouchers`,
 *    `reward_voucher`, `reward_gift_cards`, `reward_cashback`,
 *    `max_uses_per_customer`, `max_claims`, `category_ids` and the orders
 *    `scope`. Until the schema catches up, an offer exercising those branches
 *    cannot be stored, let alone rendered.
 *
 * WHAT DEPENDS ON IT. The offer card renders from an `OfferStats`; the card,
 * the five list sections, the bucketing, the scroll-to-offer, the "Offers (n)"
 * chip and the redemption overlay are all built and will light up unchanged the
 * moment this returns real stats. `useWalletActions` covers the gift-card
 * actions; the OFFER-claiming actions (`applyOfferOnOrder`,
 * `applyOfferRewardOnOrder`, `applyVoucherLinkedOffer`) are the other half that
 * needs this engine, and they are noted in that file.
 */

import type {
  CustomerRedemptionCounts,
  GiftCard,
  Offer,
  OfferWithStats,
  OrderDiscountDetails,
  Voucher,
} from "@/types/wallet";
import type { Customer } from "@/types";

export interface OffersWithStatsInput {
  offers: Offer[];
  customer: Customer | null;
  vouchers: Voucher[];
  giftCards: GiftCard[];
  customerOfferRedemptionCounts: CustomerRedemptionCounts;
  redeemed_offers: Record<string, number> | null;
  platform: string | null;
  currency_symbol?: string;
  currentItemsPrice: number;
  totalDiscountApplied: number;
  order_discount_details: OrderDiscountDetails | null;
}

export interface OffersWithStatsResult {
  offersWithStats: OfferWithStats[];
  /** The ids of every offer whose `stats.claimable` is true. */
  totalClaimableOffers: string[];
}

/**
 * Returns `null` while the engine is unported, which the provider treats
 * exactly as the original treats its own short-circuit: `offersWithStats` and
 * `totalClaimableOffers` both fall back to their stable empty arrays.
 */
export const getOffersWithStats = (
  _input: OffersWithStatsInput,
): OffersWithStatsResult | null => null;
